import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { ITransactionCostFilters } from "./TransactionCost/TransactionCostIndex";
import { SankeyDataFilter } from "./FlowAnalysis/IndexFlowAnalysis";
import { ICounterpartyReportFilters } from "./CounterpartyReport/CounterpartyReportIndex";
import { IBaseFilterBrokerRank } from "./BrokerRank/BrokerRankIndex";
import { IClientSummaryFilters } from "./ClientSummaryReport/IndexClientSummary";
import { orderMapDV01Filters } from "./BrokerRank/CurrencyAndDV01Filters";

export function filterDataGeneric<T>(
  data: T[],
  dataFilters: {
    [K in keyof T]?: T[K][] | undefined;
  },
): T[] {
  const reducedFilters = Object.fromEntries(
    Object.entries(dataFilters).filter(([key]) => {
      return key != "range";
    }),
  );

  return data.filter((item) => {
    return (Object.keys(reducedFilters) as (keyof T)[]).every((key) => {
      const values = dataFilters[key];
      // If the key exists and the filter is an empty array, exclude all items
      if (values && values.length === 0) {
        return false;
      }
      // If no filter for this key, skip filtering by it
      if (!values) return true;
      // Apply filter normally
      return values.includes(item[key]);
    });
  });
}

// // --  FILTERS -- //  //
export function filterByCurrencyAll(
  data: IBaseAnalyticsCombined[],
  dataFilters:
    | ITransactionCostFilters
    | IBaseFilterBrokerRank
    | SankeyDataFilter
    | ICounterpartyReportFilters,
) {
  const { selectedCurrencies } = dataFilters;

  // If there's no valid range, return the original data
  if (!selectedCurrencies) {
    return data;
  } else if (selectedCurrencies.length == 0) {
    return [];
  }

  return data.filter((obj) => {
    if (obj.currency) {
      return selectedCurrencies.includes(obj.currency);
    }
  });
}

export function filterByStructureAll(
  data: IBaseAnalyticsCombined[],
  dataFilters: ICounterpartyReportFilters,
) {
  const { selectedStructure } = dataFilters;

  // If there's no valid range, return the original data
  if (!selectedStructure) {
    return data;
  } else if (selectedStructure.length == 0) {
    return [];
  }

  return data.filter((obj) => {
    return selectedStructure.includes(obj.structureType);
  });
}

export function filterByDatesAffirmed(
  data: IBaseAnalyticsCombined[],
  dataFilters: IBaseFilterBrokerRank,
) {
  const { range } = dataFilters;

  // If there's no valid range, return the original data
  if (!Array.isArray(range) || range.length !== 2) {
    return data;
  }

  const startDate = new Date(range[0]);
  const endDate = new Date(range[1]);

  // Filter directly by returning the boolean expression
  return data.filter((obj: any) => {
    const objDate = new Date(obj.dateTimeExecuted);
    return objDate >= startDate && objDate <= endDate;
  });
}

export function filterByDatesTimeSent(
  data: IBaseAnalyticsCombined[],
  dataFilters:
    | ICounterpartyReportFilters
    | IBaseFilterBrokerRank
    | SankeyDataFilter
    | ITransactionCostFilters,
) {
  const { range } = dataFilters;

  // If there's no valid range, return the original data
  if (!Array.isArray(range) || range.length !== 2) {
    return data;
  }

  const startDate = new Date(range[0]);
  const endDate = new Date(range[1]);

  // Filter directly by returning the boolean expression
  return data.filter((obj) => {
    const objDate = new Date(obj.dateTimeSent);
    return objDate >= startDate && objDate <= endDate;
  });
}

// instrument FILTER
export function filterByInstrumentTypeAll(
  data: IBaseAnalyticsCombined[],
  dataFilters:
    | SankeyDataFilter
    | IClientSummaryFilters
    | ITransactionCostFilters,
) {
  const { selectedInstruments } = dataFilters;

  // If there's no valid range, return the original data
  if (!selectedInstruments) {
    return data;
  } else if (selectedInstruments.length == 0) {
    return [];
  }

  return data.filter((obj: IBaseAnalyticsCombined) => {
    return selectedInstruments.includes(obj.instrumentType);
  });
}

// Filter by security type first (if any)
// const activeSecurityTypes =
//   dataFilters?.instrumentType?.legend
//     ?.filter((item: any) => item.visible)
//     ?.map((item: any) => item.legendItemName) || [];

// const countOfInvisibleSecurityTypes =
//   dataFilters?.instrumentType?.legend?.filter((item: any) => !item.visible);

// if (
//   dataFilters?.instrumentType?.legend &&
//   dataFilters?.instrumentType?.legend.length != 0
// ) {
//   if (
//     countOfInvisibleSecurityTypes.length ==
//     dataFilters?.instrumentType?.legend.length
//   ) {
//     return [];
//   }
// }

// if (activeSecurityTypes.length > 0) {
//   const filteredData = data.filter((item: any) =>
//     activeSecurityTypes.includes(item.securityType),
//   );
//   return filteredData;
// }
// return data;
// }{{}}

// Generic function created to return Keys to map based off a string array passed in.
// Will replace the specific helper for each filter type.
export function returnKeysToMapGeneric(
  data: IBaseAnalyticsCombined[],
  keys: Array<keyof IBaseAnalyticsCombined>,
): Record<string, string[]> {
  if (!Array.isArray(data) || data.length === 0) {
    return Object.fromEntries(keys.map((key) => [key + "Keys", []]));
  }

  const result: Record<string, Set<string>> = {};

  for (const key of keys) {
    result[key] = new Set();
  }

  for (const item of data) {
    for (const key of keys) {
      const value = item[key];
      if (typeof value === "string" || typeof value === "number") {
        result[key].add(String(value));
      }
    }
  }

  const output: Record<string, string[]> = {};
  for (const key of keys) {
    output[key + "Keys"] = Array.from(result[key]);
  }
  // Adjust order for DV01 Buckets.
  // Add any other specific ordering here.
  if (output.DV01BucketsKeys) {
    output.DV01BucketsKeys = output.DV01BucketsKeys.sort((a, b) => {
      const orderA = orderMapDV01Filters[a] ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMapDV01Filters[b] ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  return output;
}
